import { IncrementPeriods } from '@siza-staff-care/shared/data';
import { DateTime } from 'luxon';
import { assertDefined } from './assert';
import { formatTimeOrNull } from './date-time-formatters';
import { Nullish } from './type-utils';

export interface Entry_With_Date_StartTime_EndTime {
	date: Date;
	startTime: Date;
	endTime: Date;
}

class Range {
	readonly start: DateTime;
	readonly end: DateTime;

	constructor(date: Date, theStart: Date, theEnd: Date) {
		const startTime = ensureDateTime(theStart);
		const endTime = ensureDateTime(theEnd);
		const crossesMidnight = Range.crossesMidnight(startTime, endTime);

		this.start = DateTime.fromJSDate(date).set({
			hour: startTime.hour,
			minute: startTime.minute,
		});
		this.end = DateTime.fromJSDate(date).set({
			hour: endTime.hour,
			minute: endTime.minute,
		});

		if (crossesMidnight) {
			this.end = this.end.plus({ days: 1 });
		}
	}

	static crossesMidnight(startTime: Date | DateTime, endTime: Date | DateTime) {
		startTime = ensureDateTime(startTime);
		endTime = ensureDateTime(endTime);
		return startTime >= endTime;
	}

	toString() {
		return `${formatTimeOrNull(this.start)} - ${formatTimeOrNull(this.end)}`;
	}
}

/**
 * Calculates the number of minutes difference between two times.
 * @param startTime The start time
 * @param endTime The end time
 * @param absolute Whether to return the absolute value.
 * @returns The number of minutes difference between the two times. Will be positive if endTime is after startTime, negative if endTime is before startTime. If absolute is true, the result will always be positive.
 */
export const minutesDifference = (startTime: Date | DateTime, endTime: Date | DateTime, absolute = false) => {
	startTime = ensureDateTime(startTime);
	endTime = ensureDateTime(endTime);

	let result = endTime.diff(startTime, 'minutes').as('minutes');
	if (absolute) {
		result = Math.abs(result);
	}

	return result;
};

/**
 * Calculates the time-value difference between a start and end time. If the end time is before
 * the start time it's assumed to cross midnight acts accordingly, unless noDayAdjust is set.
 * @param startTime
 * @param endTime
 * @param noDayAdjust
 * @returns
 */
export const timeDifference = (startTime: Date | DateTime, endTime: Date | DateTime, noDayAdjust = false) => {
	// Nasty hacky workaround for handling invalid inputs in the time fields - which results in the value being left as a string.
	if (typeof (startTime as unknown) === 'string' || typeof (endTime as unknown) === 'string') {
		return -1;
	}

	if (startTime instanceof Date) {
		startTime = DateTime.fromJSDate(startTime);
	}

	if (endTime instanceof Date) {
		endTime = DateTime.fromJSDate(endTime);
	}

	if (!startTime || !endTime) {
		return -1;
	}

	const startMinuteNumber = startTime.diff(startTime.startOf('day'), 'minutes').minutes;
	const endMinuteNumber = endTime.diff(endTime.startOf('day'), 'minutes').minutes;

	let diff = endMinuteNumber - startMinuteNumber;
	if (diff < 0 && !noDayAdjust) {
		diff += 24 * 60;
	}

	return tvToHoursMinutes(diff);
};

/**
 * Converts a minutes values into decimal time value, as stored in Jade (e.g. 450 minutes == 7.3 == 7:30)
 * @param tvMinutes
 * @returns
 */
export const tvToHoursMinutes = (tvMinutes: number) => {
	const hours = tvMinutes >= 0 ? Math.floor(tvMinutes / 60) : Math.ceil(tvMinutes / 60);
	const minutes = tvMinutes % 60;

	return Math.round((hours + minutes / 100) * 100) / 100;
};

/**
 * Determines whether the Date represents midnight.
 * @param {*} time
 * @returns
 * @memberof Utils
 */
export const isMidnightTime = (time: Date | DateTime | Nullish) => {
	if (!time) {
		return true;
	}

	if (time instanceof DateTime) {
		time = time.toJSDate();
	}

	return time.getHours() === 0 && time.getMinutes() === 0 && time.getSeconds() === 0;
};

/**
 * Returns a new Date with the time set to midnight today.
 * @returns
 */
export const newMidnightTime = () => {
	const now = new Date();
	return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/**
 * Determines whether the start and end times represent a user-defined-times shift.
 * @param startTime
 * @param endTime
 * @returns
 */
export const isShiftUserDefinedTimes = (startTime: Date, endTime: Date) => {
	if (!startTime || !endTime) {
		return false;
	}

	return (
		startTime.getHours() === 0 &&
		startTime.getMinutes() === 0 &&
		endTime.getHours() === 0 &&
		endTime.getMinutes() === 0
	);
};

/**
 * Determines whether the start and end times represent an empty time range - that is 00:00 - 00:00
 * @param startTime
 * @param endTime
 * @returns
 */
export const isEmptyTimeRange = (startTime: Date | DateTime, endTime: Date | DateTime) => {
	const start = ensureJsDate(startTime);
	const end = ensureJsDate(endTime);

	return isShiftUserDefinedTimes(start, end);
};

/**
 * Adds a decimal time value (e.g. 7.3 == 7h30m) to another decimal time value
 * @param  {Number} toValue The value to add tv to
 * @param  {Number} tv The decimal time value to add.
 */
export const addTimeValue = (toValue: number, tv: number) => {
	const tvMinutes = tvToMinutes(tv),
		valueMinutes = tvToMinutes(toValue);

	const resultMinutes = valueMinutes + tvMinutes;
	const result = tvToHoursMinutes(resultMinutes);

	return result;
};

/**
 * Convert a decimal time value (e.g. 7.3 == 7h30m) to minutes.
 * 7.3 -> 450
 * @param  {Number} tv
 * @returns {Number}
 */
export const tvToMinutes = (tv: number) => {
	const isNeg = tv < 0;
	tv = Math.abs(tv);

	const hours = Math.floor(tv);
	const minutes = hours * 60 + (Math.floor(Math.round(tv * 100)) % 100);

	return isNeg ? -minutes : minutes;
};

/**
 * Converts a number of minutes into a Staff.Care time decimal value.
 * e.g. 90 minutes -> 1.3.
 * @param {*} minutes
 * @returns
 */
export const convertMinutesToTimeValue = (minutes: number) => {
	return convertTrueDecimalToTimeValue(minutes / 60);
};

/**
 * Converts a value, which is true decimal, into Staff.Care time decimal.
 * E.g. 262.5 represents 262:30 - so needs to become 262.3
 * @param {*} tvTrueDecimal
 */
export const convertTrueDecimalToTimeValue = (tvTrueDecimal: number) => {
	if (tvTrueDecimal === undefined || isNaN(tvTrueDecimal)) {
		return tvTrueDecimal;
	}

	const isNeg = tvTrueDecimal < 0;
	tvTrueDecimal = Math.abs(tvTrueDecimal);

	const hoursPart = Math.floor(tvTrueDecimal);
	const decimalPart = tvTrueDecimal - Math.floor(tvTrueDecimal);
	const fractionalMinutes = (decimalPart * 60) / 100;
	let result = hoursPart + fractionalMinutes;

	if (isNeg) {
		result = result * -1;
	}
	return result;
};

/**
 * Converts a time value, in Staff.Care time decimal into a true decimal value.
 * e.g. 262.3 represents 262:30 - so it becomes 262.5 - i.e. 262 and a half hours.
 * @param {*} tv
 */
export const convertTimeValueToTrueDecimal = (tv: number) => {
	if (tv === undefined || isNaN(tv)) {
		return tv;
	}

	const totalMinutes = tv * 100;
	const hours = Math.floor(totalMinutes / 100);
	const minutes = totalMinutes % 100;

	const result = hours + minutes / 60;
	return result;
};

export const makeTimeStamp = (
	datePart: Date | DateTime | undefined,
	timePart: Date | DateTime | undefined,
	requireBoth = false,
) => {
	if (!datePart) {
		return null;
	}

	datePart = ensureJsDate(datePart);
	const d = DateTime.fromJSDate(datePart).startOf('day');

	if (!timePart) {
		if (requireBoth) {
			return null;
		}
		return d.toJSDate();
	}

	timePart = ensureJsDate(timePart);
	const tm = DateTime.fromJSDate(timePart);
	const result = d
		.set({ hour: tm.hour, minute: tm.minute, second: tm.second, millisecond: tm.millisecond })
		.toJSDate();
	return result;
};

/**
 * Takes a Date or DateTime and returns a Date.
 * @param date
 * @returns
 */
export const ensureJsDate = (date: Date | DateTime) => {
	if (date instanceof DateTime) {
		return date.toJSDate();
	}

	return date;
};

/**
 * Takes a Date or DateTime and returns a DateTime.
 * @param date
 * @returns
 */
export const ensureDateTime = (date: Date | DateTime) => {
	if (date instanceof Date) {
		return DateTime.fromJSDate(date);
	}

	return date;
};

/**
 * Determines whether a date is valid or not.
 * @param date
 * @returns
 */
export const isValidDate = (date: Date | DateTime) => {
	if (date instanceof DateTime) {
		return date.isValid;
	}

	return !isNaN(date.getTime());
};

/**
 * Returns the absolute number of whole days between two dates.
 * @param date1
 * @param date2
 * @returns
 */
export const daysBetween = (date1: Date | DateTime, date2: Date | DateTime) => {
	date1 = ensureJsDate(date1);
	date2 = ensureJsDate(date2);

	const diff = Math.abs(date1.getTime() - date2.getTime());
	const daysDiff = Math.round(diff / (1000 * 3600 * 24));
	return Math.floor(daysDiff);
};

/**
 * Add a number of days to a date.
 * @param toDate
 * @param daysToAdd
 * @returns
 */
export const addDays = (toDate: Date | DateTime, daysToAdd: number) => {
	toDate = ensureJsDate(toDate);
	const newDate = DateTime.fromJSDate(toDate).plus({ day: daysToAdd }).toJSDate();
	return newDate;
};

/**
 * Returns whether the first date is before the second date.
 * @param date1
 * @param date2
 * @returns
 */
export const isBefore = (date1: Date | DateTime | null | undefined, date2: Date | DateTime | null | undefined) => {
	if (!date1 || !date2) {
		return false;
	}

	date1 = ensureDateTime(date1);
	date2 = ensureDateTime(date2);

	return date1 < date2;
};

/**
 * Returns whether the first date is after the second date.
 * @param date1
 * @param date2
 * @returns
 */
export const isAfter = (date1: Date | DateTime | null | undefined, date2: Date | DateTime | null | undefined) => {
	if (!date1 || !date2) {
		return false;
	}

	date1 = ensureDateTime(date1);
	date2 = ensureDateTime(date2);

	return date1 > date2;
};

/**
 * Returns whether the first date/time is the same as the second date/time (to the millisecond).
 * @param date1
 * @param date2
 * @returns
 */
export const isSame = (date1: Date | DateTime | null | undefined, date2: Date | DateTime | null | undefined) => {
	if (!date1 || !date2) {
		return false;
	}

	date1 = ensureDateTime(date1);
	date2 = ensureDateTime(date2);

	return date1.equals(date2);
};

/**
 * Returns whether the first date is the same as the second date.
 * @param date1
 * @param date2
 * @returns
 */
export const isSameDate = (date1: Date | DateTime | null | undefined, date2: Date | DateTime | null | undefined) => {
	if (!date1 || !date2) {
		return false;
	}

	date1 = ensureDateTime(date1);
	date2 = ensureDateTime(date2);

	return date1.hasSame(date2, 'day');
};

export const isSameOrAfterDate = (
	date1: Date | DateTime | null | undefined,
	date2: Date | DateTime | null | undefined,
) => {
	return isSameDate(date1, date2) || isAfter(date1, date2);
};

export const isSameOrBeforeDate = (
	date1: Date | DateTime | null | undefined,
	date2: Date | DateTime | null | undefined,
) => {
	return isSameDate(date1, date2) || isBefore(date1, date2);
};

/**
 * Returns true if both dates have the same time (to the millisecond). Date is ignored.
 * @param date1
 * @param date2
 * @returns
 */
export const isSameTime = (date1: Date | DateTime | null | undefined, date2: Date | DateTime | null | undefined) => {
	if (!date1 || !date2) {
		return false;
	}

	date1 = ensureDateTime(date1);
	date2 = ensureDateTime(date2);

	return (
		date1.hour === date2.hour &&
		date1.minute === date2.minute &&
		date1.second === date2.second &&
		date1.millisecond === date2.millisecond
	);
};

export const rollDate = (date: Date | DateTime, increment: number, incrementPeriod: string): Date => {
	const m = ensureDateTime(date);
	const momentPeriods = {
		D: 'days',
		W: 'weeks',
		M: 'months',
		Y: 'years',
	};

	return m.plus({ [momentPeriods[incrementPeriod]]: increment }).toJSDate();
};

/**
 * Returns an array of entries which at least partially overlap a time range.
 * @param  {Date} date The date
 * @param  {Object} overlapOn The object with startTime and endTime properties
 * @param  {Array} entries The entries with date, and startTime and endTime properties to filter.
 * @param  {Boolean} ignoreUserDefTimes Whether to find overlaps even on a user-defined-times shift.
 */
export const getOverlaps = <T extends Entry_With_Date_StartTime_EndTime>(
	date: Date,
	overlapOn: { startTime: Date; endTime: Date },
	entries: T[],
	ignoreUserDefTimes = false,
) => {
	// Don't allow shifts with user-defined times to overlap with entries.
	if (!ignoreUserDefTimes && isShiftUserDefinedTimes(overlapOn.startTime, overlapOn.endTime)) {
		return;
	}

	const result: T[] = [],
		overlapRange = makeRange(date, overlapOn.startTime, overlapOn.endTime);

	for (let i = 0; i < entries.length; i++) {
		const entry = entries[i],
			entryRange = makeRange(entry.date, entry.startTime, entry.endTime);

		if (rangesOverlap(overlapRange, entryRange)) {
			result.push(entry);
		}
	}

	return result;
};

/**
 * Makes a date/time range object
 * @param  {Date} date The date
 * @param  {Date} theStart The start time (date part ignored)
 * @param  {Date} theEnd The end time (date part ignored)
 */
export const makeRange = (date: Date, theStart: Date, theEnd: Date) => {
	return new Range(date, theStart, theEnd);
};

/**
 * Returns true if the time range crosses midnight.
 * @param {Date} startTime
 * @param {Date} endTime
 */
export const crossesMidnight = (startTime: Date, endTime: Date) => {
	return Range.crossesMidnight(startTime, endTime);
};

/**
 * Returns true if the ranges overlap (at least partially)
 * @param  {Object} range1
 * @param  {Object} range2
 */
export const rangesOverlap = (range1: Range, range2: Range) => {
	return (
		ensureDateTime(range1.start) < ensureDateTime(range2.end) &&
		ensureDateTime(range1.end) > ensureDateTime(range2.start)
	);
};

/**
 * Returns true if testRange wholly covers otherRange.
 * @param  {Object} testRange The range to check whether it covers
 * @param  {Object} otherRange The other range that testRange must cover.
 */
export const rangeCovers = (testRange: Range, otherRange: Range) => {
	return (
		!(ensureDateTime(testRange.start) > ensureDateTime(otherRange.start)) &&
		!(ensureDateTime(testRange.end) < ensureDateTime(otherRange.end))
	);
};

/**
 * Returns the number of milliseconds between now and another time, with a minimum of the ms parameter.
 * @param minimumMs
 * @param otherTime
 * @returns
 */
export const minMsSince = (minimumMs: number, otherTime: Date): number => {
	// Determine the number of milliseconds since the other time.
	const timeSince = new Date().getTime() - otherTime.getTime();

	// If the time since is less than the minimum, return the difference, otherwise return 0 since the minimum time has been exceeded.
	const result = timeSince < minimumMs ? minimumMs - timeSince : 0;
	return result;
};

/**
 * Combines a date (forces start of day) and time (removing the date part) into a Date.
 * @param {Date} date
 * @param {Date} time
 * @param {Date} otherDate Optional other date to compare to. If the result is before otherDate then add 1 day.
 */
export const buildTimestampFromDateAndTime = (
	date: Date | DateTime,
	time: Date | undefined,
	otherDate?: Date | DateTime,
) => {
	date = ensureJsDate(date);
	otherDate = otherDate ? ensureDateTime(otherDate) : undefined;

	if (!time) {
		return undefined;
	}

	const timeMs = ensureDateTime(time);
	const ms = timeMs.diff(timeMs.startOf('day')).toMillis();

	let result = DateTime.fromJSDate(date).startOf('day').plus({ milliseconds: ms });

	if (otherDate && isBefore(result, otherDate)) {
		result = result.plus({ day: 1 });
	}

	return result.toJSDate();
};

/**
 * Makes an object with start and end properties from a date and start and end times.
 * @param date
 * @param startTime
 * @param endTime
 * @returns
 */
export const makeTimeStamps = (date: Date, startTime: Date, endTime: Date) => {
	const shiftStart = buildTimestampFromDateAndTime(date, startTime);
	let shiftEnd = buildTimestampFromDateAndTime(date, endTime);

	assertDefined(shiftStart);
	assertDefined(shiftEnd);

	if (shiftEnd < shiftStart) {
		shiftEnd = addDays(shiftEnd, 1);
	}

	return {
		start: shiftStart,
		end: shiftEnd,
	};
};

export const getDate = (date: Date | DateTime) => {
	return ensureDateTime(date).startOf('day').toJSDate();
};

export const addDaysToTimeStamps = (timeStamps: { start: Date; end: Date }, days: number) => {
	return {
		start: addDays(timeStamps.start, days),
		end: addDays(timeStamps.end, days),
	};
};

/**
 * Builds an array of dates between and including dateStart and dateEnd
 * @param  {Date} dateStart The first date
 * @param  {Date} dateEnd The last date
 */
export const buildDatesArray = (dateStart: Date, dateEnd: Date): Date[] => {
	const dates: Date[] = [];

	let d = ensureDateTime(dateStart);
	while (!isAfter(d, dateEnd)) {
		dates.push(d.toJSDate());
		d = d.plus({ days: 1 });
	}

	return dates;
};

/**
 * Returns true if date is before today.
 * @param  {Date} date The date to compare to today.
 */
export const isDatePast = (date: Date | DateTime) => {
	if (!date) {
		return false;
	}

	const today = ensureDateTime(new Date()).startOf('day');
	return isBefore(date, today);
};

/**
 * Returns true if the date is a weekday.
 * @param  {Date} date The date to test.
 */
export const isDateWeekday = (date: Date | DateTime) => {
	if (!date) {
		return false;
	}

	const theDate = ensureDateTime(date);
	// Get day of week
	const dayOfWeek = theDate.weekday;
	return dayOfWeek >= 1 && dayOfWeek <= 5;
};

/**
 * Returns true if the date is a weekend.
 * @param  {Date} date The date to test.
 */
export const isDateWeekend = (date: Date | DateTime) => {
	if (!date) {
		return false;
	}

	return !isDateWeekday(date);
};

export const formatIncrement = (increment: number, incrementPeriod: string) => {
	const periodName = IncrementPeriods.find((p) => p.code === incrementPeriod)?.description;
	return `${increment} ${periodName}`;
};
